/**
 * PickingSettingsSection — picking mode, auto-batch default size, per-location
 * verification mode, and scanner toggle (extracted from the old god
 * component's `renderPickingSettings`).
 *
 * Mixed-scope section: picking mode / auto-batch size / scanner are BROWSER
 * (useProductionSettings localStorage); verification mode is PER-LOCATION
 * (bound to the header LocationFilter selection via useLocationPickingMode).
 * Follows the StationSettingsSection dirty-model exemplar — pending local
 * state, one `save()` that applies both kinds of update and throws on
 * mutation failure.
 */
import { useEffect, useRef, useState } from 'react';
import { ScanLine } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLocation } from '@/contexts/LocationContext';
import {
  useLocationPickingMode,
  useUpdateLocationPickingMode,
  buildUpdateModeInput,
} from '@/features/production-orders/hooks/usePicking';
import type { PickingVerificationMode } from '@/features/production-orders/types';
import { useProductionSettings } from '../hooks/useProductionSettings';
import { SettingsSection } from '../components/console/SettingsSection';
import { SettingRow, SettingRowDivider } from '../components/console/SettingRow';
import { useDirtyRegistration } from '../components/console/SettingsDirtyContext';

interface PendingPicking {
  pickingMode?: 'batch_pick' | 'pick_by_line';
  defaultBatchSize?: number;
  showPickingScanner?: boolean;
  verificationMode?: PickingVerificationMode;
}

export function PickingSettingsSection() {
  const { settings, updateSetting } = useProductionSettings();
  const { selectedLocation } = useLocation();
  const { data: verificationMode, isLoading: verificationLoading } = useLocationPickingMode();
  const updatePickingMode = useUpdateLocationPickingMode();

  const [pending, setPending] = useState<PendingPicking>({});

  // The verification row binds to the header location. If that location
  // changes while an edit is pending, the edit no longer targets anything
  // real — drop it rather than silently applying it to the new location.
  const locationIdRef = useRef(selectedLocation?.id);
  useEffect(() => {
    if (locationIdRef.current === selectedLocation?.id) return;
    locationIdRef.current = selectedLocation?.id;
    setPending((p) => {
      if (p.verificationMode === undefined) return p;
      const { verificationMode: _drop, ...rest } = p;
      return rest;
    });
  }, [selectedLocation?.id]);

  const savedVerification: PickingVerificationMode = verificationMode ?? 'none';
  const pickingModeValue = pending.pickingMode ?? settings.pickingMode;
  const defaultBatchSizeValue = pending.defaultBatchSize ?? settings.defaultBatchSize;
  const showScannerValue = pending.showPickingScanner ?? settings.showPickingScanner;
  const verificationValue = pending.verificationMode ?? savedVerification;

  const isDirty =
    (pending.pickingMode !== undefined && pending.pickingMode !== settings.pickingMode) ||
    (pending.defaultBatchSize !== undefined && pending.defaultBatchSize !== settings.defaultBatchSize) ||
    (pending.showPickingScanner !== undefined &&
      pending.showPickingScanner !== settings.showPickingScanner) ||
    (pending.verificationMode !== undefined && pending.verificationMode !== savedVerification);

  useDirtyRegistration(
    'picking',
    isDirty
      ? {
          label: 'Picking',
          save: async () => {
            if (pending.verificationMode !== undefined && selectedLocation) {
              await updatePickingMode.mutateAsync(
                buildUpdateModeInput(selectedLocation.id, pending.verificationMode),
              );
            }
            if (pending.pickingMode !== undefined) {
              updateSetting('pickingMode', pending.pickingMode);
            }
            if (pending.defaultBatchSize !== undefined) {
              updateSetting('defaultBatchSize', pending.defaultBatchSize);
            }
            if (pending.showPickingScanner !== undefined) {
              updateSetting('showPickingScanner', pending.showPickingScanner);
            }
            setPending({});
          },
          discard: () => setPending({}),
        }
      : null,
  );

  return (
    <SettingsSection
      id="picking"
      title="Picking"
      description="Picking mode, auto-batch default size, verification and scanner."
      icon={ScanLine}
      scope="browser"
    >
      <div className="flex flex-col">
        <SettingRow
          htmlFor="picking-mode"
          label="Picking mode"
          description="How items are organized for picking."
          scope="browser"
        >
          <Select
            value={pickingModeValue}
            onValueChange={(value: 'batch_pick' | 'pick_by_line') =>
              setPending((p) => ({ ...p, pickingMode: value }))
            }
          >
            <SelectTrigger
              id="picking-mode"
              className="w-full sm:w-[240px] h-10"
              aria-label="Select picking mode"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="batch_pick">Batch Pick (by SKU)</SelectItem>
              <SelectItem value="pick_by_line">Pick by Line Item</SelectItem>
            </SelectContent>
          </Select>
        </SettingRow>

        <SettingRowDivider />

        <SettingRow
          htmlFor="default-batch-size"
          label="Auto-batch default size"
          description="Default garment count when auto-building a pick batch. The org-wide 'Maximum batch size' cap in Workflow rules still applies."
          scope="browser"
        >
          <Input
            id="default-batch-size"
            type="number"
            inputMode="numeric"
            min={10}
            max={500}
            className="w-full sm:w-[120px] h-10 text-center text-sm font-medium"
            value={defaultBatchSizeValue}
            onChange={(e) => {
              const value = parseInt(e.target.value, 10) || 50;
              const clamped = Math.max(10, Math.min(500, value));
              setPending((p) => ({ ...p, defaultBatchSize: clamped }));
            }}
            aria-describedby="batch-size-range"
          />
          <span id="batch-size-range" className="sr-only">
            Enter a number between 10 and 500
          </span>
        </SettingRow>

        <SettingRowDivider />

        <SettingRow
          htmlFor="picking-verification-mode"
          label="Verification mode"
          description={
            !selectedLocation
              ? 'Select a location above to edit this setting.'
              : verificationValue === 'required'
                ? 'Pickers scan each garment at the picker station to confirm the pick.'
                : 'No confirmation step: pickers print the labels, apply them and move on. Scan-to-confirm is available on request.'
          }
          scope="location"
        >
          <Select
            value={verificationValue}
            onValueChange={(value: PickingVerificationMode) =>
              setPending((p) => ({ ...p, verificationMode: value }))
            }
            disabled={!selectedLocation || verificationLoading || updatePickingMode.isPending}
          >
            <SelectTrigger
              id="picking-verification-mode"
              className="w-full sm:w-[220px] h-10"
              aria-label="Select picking verification mode"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None (Default)</SelectItem>
              {/* BLU-747: scan-to-confirm is no longer self-serve. No floor was using
                  it (every location resolves to 'none'), and switching a site into it
                  silently adds a per-garment scan step the pickers have never done.
                  Left visible so the capability is discoverable, and still selectable
                  for any location already on it so nobody gets trapped. */}
              <SelectItem value="required" disabled={savedVerification !== 'required'}>
                Required (on request)
              </SelectItem>
            </SelectContent>
          </Select>
        </SettingRow>

        {verificationValue === 'required' ? (
          <>
            <SettingRowDivider />
            <SettingRow
              htmlFor="show-picking-scanner"
              label="QR scanner on batch page"
              description="Show the QR scanner input on the batch detail page for scan-to-verify."
              scope="browser"
            >
              <Switch
                id="show-picking-scanner"
                checked={showScannerValue}
                onCheckedChange={(checked) =>
                  setPending((p) => ({ ...p, showPickingScanner: checked }))
                }
              />
            </SettingRow>
          </>
        ) : null}
      </div>
    </SettingsSection>
  );
}
